import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAvailableMetrics, metricOptions } from '../../../Data/Reports';
import { usePreference } from '../../../Data/Preference';
import { useServices } from '../../../Data/Services';
import { useGrants } from '../../../Data/Business';
import { attentionStatuses } from '../../../Data/Conversations';
import API from '../../../Services/API';
import Connectivity from '../../../Services/Connectivity';
import { useNewWindow } from '../../../Services/helpers';
import { useToasts } from '../../../Components/Toasts';

const MetricContext = createContext();
const METRICS_BATCH_SIZE = 5;
const METRICS_POOL_SIZE = 3;
const NO_OPTIONS = {};
const DATE_KEYS = ['dateRange', 'dateInterval'];

export const useMetricContext = () => {
    return useContext(MetricContext);
};

export const MetricsProvider = ({ children, metrics: providedMetricsConfig = null, options: optionsConfig, controls: controlKeys = null, dateRange: dateRangeConfig = null, preferenceScope = 'metrics' }) => {
    const {availableMetrics} = useAvailableMetrics(false);
    const [dateRange, setDateRange] = usePreference(dateRangeConfig?.preference ?? 'metrics:dateRange', dateRangeConfig?.default ?? 'lastThreeMonths');
    const [dateInterval, setDateInterval] = usePreference('metrics:dateInterval', 'auto');
    const [storedOptions, setStoredOptions] = usePreference(`${preferenceScope}:options`, NO_OPTIONS);
    const [cardOptions, setCardOptions] = usePreference(`${preferenceScope}:cardOptions`, NO_OPTIONS);
    const {services} = useServices(false);
    const {grants} = useGrants();
    const [downloadingMetric, setDownloadingMetric] = useState(null);
    const newWindow = useNewWindow();
    const {showError} = useToasts();

    const metricsConfig = useMemo(() => {
        return (providedMetricsConfig ?? []).map((metric, index) => ({
            ...metric,
            key: metric.key ?? `${metric.name}:${index}`,
        }));
    }, [providedMetricsConfig]);

    const catalogue = useMemo(() => {
        return (availableMetrics ?? []).reduce((acc, metric) => ({...acc, [metric.name]: metric}), {});
    }, [availableMetrics]);

    const dynamicChoices = useMemo(() => ({
        userId: [
            {value: null, label: metricOptions.userId.allLabel},
            ...workerChoices(grants),
        ],
        service: [
            {value: null, label: metricOptions.service.allLabel},
            ...(services ?? []).map(service => ({value: service.type, label: service.title})),
        ],
        attentionStatus: [
            {value: null, label: metricOptions.attentionStatus.allLabel},
            ...Object.entries(attentionStatuses).map(([value, status]) => ({value, label: status.name})),
        ],
    }), [grants, services]);

    const choicesFor = (key, metricNames) => {
        if (dynamicChoices[key]) {
            return dynamicChoices[key];
        }

        const allowed = metricNames.flatMap(name => allowedValues(catalogue[name], key) ?? (metricOptions[key]?.choices ?? []).map(c => c.value));

        return (metricOptions[key]?.choices ?? []).filter(choice => allowed.includes(choice.value));
    };

    const controls = useMemo(() => {
        if (!availableMetrics) {
            return [];
        }

        const keys = controlKeys ?? Object.keys(metricOptions).filter(key => metricsConfig.some(m => takesOption(catalogue[m.name], key)));

        return [...new Set(['dateRange', ...keys])].flatMap(key => {
            const takers = metricsConfig.filter(m => takesOption(catalogue[m.name], key) && !(key in (m.options ?? {})) && !(key in (optionsConfig ?? {})));

            if (key === 'dateRange') {
                return takers.length > 0 ? [{key, value: dateRange, setValue: setDateRange, ranges: dateRangeConfig?.ranges ?? 'past'}] : [];
            }

            if (key === 'dateInterval') {
                return takers.length > 0 ? [{key, ...metricOptions.dateInterval, value: dateInterval, setValue: setDateInterval, choices: metricOptions.dateInterval.choices}] : [];
            }

            const choices = choicesFor(key, takers.map(m => m.name));

            if (takers.length === 0 || choices.length < 2) {
                return [];
            }

            return [{
                key,
                ...metricOptions[key],
                choices,
                value: pickValue(choices, storedOptions[key]),
                setValue: (value) => setStoredOptions({...storedOptions, [key]: value}),
            }];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableMetrics, catalogue, controlKeys, metricsConfig, dateRange, dateInterval, storedOptions, dynamicChoices, dateRangeConfig, optionsConfig]);

    const requests = useMemo(() => {
        if (!availableMetrics) {
            return null;
        }

        const pageValues = controls.reduce((acc, control) => ({...acc, [control.key]: control.value}), {});

        return metricsConfig.filter(m => catalogue[m.name]).map(metric => {
            const definition = catalogue[metric.name];
            const options = {};

            Object.keys(definition.options ?? {}).forEach(key => {
                if (key in (metric.options ?? {})) {
                    options[key] = metric.options[key];
                    return;
                }

                if (key in (optionsConfig ?? {})) {
                    options[key] = optionsConfig[key];
                    return;
                }

                const allowed = allowedValues(definition, key);
                const fromCard = cardOptions[metric.key]?.[key];
                const fromPage = pageValues[key];

                if (DATE_KEYS.includes(key)) {
                    options[key] = fromPage ?? (key === 'dateRange' ? dateRange : dateInterval);
                    return;
                }

                if (fromCard !== undefined && (allowed === null || allowed.includes(fromCard))) {
                    setIfPresent(options, key, fromCard);
                    return;
                }

                if (fromPage !== undefined && (allowed === null || allowed.includes(fromPage))) {
                    setIfPresent(options, key, fromPage);
                    return;
                }

                if (allowed !== null && isRequired(definition, key)) {
                    options[key] = preferredValue(key, allowed);
                }
            });

            return {
                key: metric.key,
                name: metric.name,
                options,
                compare: metric.compare === true,
            };
        });
    }, [availableMetrics, catalogue, metricsConfig, controls, cardOptions, optionsConfig, dateRange, dateInterval]);

    const [metrics, setMetrics] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const showErrorRef = useRef(showError);
    const requestsKey = JSON.stringify(requests);

    useEffect(() => {
        showErrorRef.current = showError;
    }, [showError]);

    useEffect(() => {
        if (requests === null) {
            return () => {};
        }

        let cancelled = false;
        let activeRequests = 0;
        let nextChunkIndex = 0;
        let hasFailures = false;
        const indexed = requests.map((request, index) => ({request, index}));
        const resolved = Array(indexed.length).fill(null);

        if (indexed.length === 0) {
            setMetrics([]);
            setIsLoading(false);
            return () => {};
        }

        const chunks = chunkMetrics(indexed, METRICS_BATCH_SIZE);

        setIsLoading(true);

        const failed = (item) => ({
            ...item.request,
            error: 'Metric failed to load.',
        });

        const showProgress = () => {
            setMetrics(current => indexed.map((item, index) => {
                return resolved[index]
                    ?? current?.find(metric => metric.key === item.request.key)
                    ?? {...item.request, pending: true, resultType: catalogue[item.request.name]?.resultType};
            }));
        };

        const maybeFinish = () => {
            if (cancelled || nextChunkIndex < chunks.length || activeRequests > 0) {
                return;
            }

            setMetrics(resolved.map((result, index) => result ?? failed(indexed[index])));
            setIsLoading(false);

            // Don't nag about failures we expect while offline — the request
            // couldn't reach the network, not a genuine metrics problem.
            if (hasFailures && Connectivity.online !== false) {
                showErrorRef.current('Some metrics failed to load. Please try refreshing.');
            }
        };

        const runNext = () => {
            if (cancelled || nextChunkIndex >= chunks.length) {
                maybeFinish();
                return;
            }

            const chunk = chunks[nextChunkIndex];
            nextChunkIndex += 1;
            activeRequests += 1;

            return API.post('/reports/metrics', {
                metrics: chunk.map(item => ({name: item.request.name, options: item.request.options, compare: item.request.compare})),
            }).then(results => {
                if (cancelled) {
                    return;
                }

                chunk.forEach((item, index) => {
                    const result = Array.isArray(results) ? results[index] : null;

                    if (!result) {
                        hasFailures = true;
                        resolved[item.index] = failed(item);
                        return;
                    }

                    resolved[item.index] = {...result, key: item.request.key};
                });

                showProgress();
            }).catch(() => {
                hasFailures = true;
                chunk.forEach(item => {
                    resolved[item.index] = failed(item);
                });
            }).finally(() => {
                activeRequests -= 1;
                runNext();
                maybeFinish();
            });
        };

        showProgress();

        const poolSize = Math.min(METRICS_POOL_SIZE, chunks.length);
        for (let i = 0; i < poolSize; i += 1) {
            runNext();
        }

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestsKey]);

    const cardControlsFor = (metric, config) => {
        const definition = catalogue[metric.name];

        if (!definition) {
            return [];
        }

        return Object.keys(definition.options ?? {})
            .filter(key => !DATE_KEYS.includes(key))
            .filter(key => !(key in (config.options ?? {})))
            .filter(key => !controls.some(control => control.key === key))
            .flatMap(key => {
                const allowed = allowedValues(definition, key);
                const choices = dynamicChoices[key] ?? (metricOptions[key]?.choices ?? []).filter(choice => allowed === null || allowed.includes(choice.value));

                if (choices.length < 2) {
                    return [];
                }

                return [{
                    key,
                    label: metricOptions[key]?.label ?? key,
                    choices,
                    value: metric.options?.[key] ?? null,
                    setValue: (value) => setCardOptions({...cardOptions, [metric.key]: {...(cardOptions[metric.key] ?? {}), [key]: value}}),
                }];
            });
    };

    const metricsWithConfig = useMemo(() => {
        if (!metrics) {
            return metrics;
        }

        return metrics.map(metric => {
            const config = metricsConfig.find(m => m.key === metric.key) ?? {};

            return {
                ...metric,
                config,
                cardControls: cardControlsFor(metric, config),
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metrics, metricsConfig, controls, cardOptions, catalogue, dynamicChoices]);

    const downloadMetric = (metric) => {
        setDownloadingMetric(metric.key ?? metric.name);
        API.post('/reports/metrics/download', {
            metric: metric.name,
            options: metric.options,
        }).then(res => {
            newWindow(res.downloadUrl);
        }).catch(
            error => showError(API.parseErrorResponse(error) ?? 'Error downloading metric')
        ).finally(() => setDownloadingMetric(null));
    };

    const period = useMemo(() => {
        return metrics?.find(metric => metric.period)?.period ?? null;
    }, [metrics]);

    const providerValues = {
        metrics: metricsWithConfig,
        isLoading,
        isReady: !!availableMetrics,
        controls,
        period,
        dateRange,
        setDateRange,
        dateInterval,
        setDateInterval,
        downloadMetric,
        downloadingMetric,
    };

    return <MetricContext.Provider value={providerValues}>
        {children}
    </MetricContext.Provider>;
};

const rulesFor = (definition, key) => {
    const rule = definition?.options?.[key];

    if (typeof rule === 'string') {
        return rule.split('|');
    }

    if (Array.isArray(rule)) {
        return rule.filter(part => typeof part === 'string');
    }

    return [];
};

const workerChoices = (grants) => {
    const people = (grants ?? []).filter(grant => grant.userId);
    const isShared = (title) => people.filter(grant => grant.title === title).length > 1;

    return people
        .map(grant => {
            const email = grant.email ?? grant.userEmail;

            return {value: grant.userId, label: (isShared(grant.title) && email) ? `${grant.title} (${email})` : grant.title};
        })
        .sort((a, b) => a.label.localeCompare(b.label));
};

const takesOption = (definition, key) => {
    return !!definition?.options && key in definition.options;
};

const isRequired = (definition, key) => {
    return rulesFor(definition, key).includes('required');
};

const allowedValues = (definition, key) => {
    const rule = rulesFor(definition, key).find(part => part.startsWith('in:'));

    if (!rule) {
        return null;
    }

    const values = rule.substring(3).split(',');

    return isRequired(definition, key) ? values : [...values, null];
};

const setIfPresent = (options, key, value) => {
    if (value !== null) {
        options[key] = value;
    }
};

const preferredValue = (key, allowed) => {
    return (metricOptions[key]?.choices ?? []).find(choice => allowed.includes(choice.value))?.value ?? allowed[0];
};

const pickValue = (choices, value) => {
    if (choices.some(choice => choice.value === value)) {
        return value;
    }

    return choices[0]?.value ?? null;
};

const chunkMetrics = (metrics, chunkSize) => {
    const chunks = [];

    for (let i = 0; i < metrics.length; i += chunkSize) {
        chunks.push(metrics.slice(i, i + chunkSize));
    }

    return chunks;
};
